import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // In older Safari versions, clicking interactive elements inside <summary> can behave inconsistently
    // (sometimes toggling <details> without following the link). We handle link navigation ourselves
    // to avoid needing "View all" fallback links in drawer submenus.
    this.addEventListener('click', this.#onClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Ensure links inside <summary> navigate (and don't toggle) across browsers (notably older Safari).
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-accordion-link]');
    if (!(link instanceof HTMLAnchorElement)) return;

    // Only intercept when the link is inside a <summary> (drawer accordions / submenus)
    if (!link.closest('summary')) return;

    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    event.stopPropagation();

    // Respect explicit target; default to same-tab navigation.
    if (link.target && link.target !== '_self') {
      window.open(href, link.target, 'noopener');
    } else {
      window.location.assign(href);
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      const panel = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu');
      if (!(panel instanceof HTMLElement)) return;
      const settle = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', settle);
        if (details.classList.contains('menu-open')) details.classList.add('menu-drawer-settled');
      };
      panel.addEventListener('transitionend', settle);
      window.setTimeout(() => {
        if (details.classList.contains('menu-open')) details.classList.add('menu-drawer-settled');
      }, 450);
    });
    if (details === this.refs.details) {
      document.documentElement.classList.add('sabo-menu-open');
    }

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-drawer-settled');
    const panel = details.querySelector(':scope > .menu-drawer, :scope > .menu-drawer__submenu');
    if (panel instanceof HTMLElement) void panel.offsetWidth;
    details.classList.remove('menu-open');
    if (details === this.refs.details) {
      document.documentElement.classList.remove('sabo-menu-open');
    }

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'menu-drawer-settled');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  if (element.classList.contains('menu-drawer-container')) {
    document.documentElement.classList.remove('sabo-menu-open');
  }
}
